import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { contentAPI, GeneratedContent, Category } from '../services/contentAPI';
import { imageUploadService } from '../services/imageUpload';
import CustomJoditEditor from '../components/JoditEditor';
import ReadOnlyJoditEditor from '../components/ReadOnlyJoditEditor';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

// PDF.js worker 설정
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const BlogWrite: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState({
    category_id: 1,
    topic: ''
  });
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState('');
  const [isProcessingFile, setIsProcessingFile] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const categoriesData = await contentAPI.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('카테고리 로딩 실패:', err);
      setError('카테고리를 불러오는데 실패했습니다.');
    }
  };


  const handleReset = () => {
    setGeneratedContent(null);
    setFormData({
      category_id: 1,
      topic: ''
    });
    setEditorContent('');
    setError(null);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();

    if (!fileName.endsWith('.pdf') && !fileName.endsWith('.docx') && !fileName.endsWith('.doc')) {
      setError('PDF 또는 DOCX 파일만 업로드 가능합니다.');
      return;
    }

    setIsProcessingFile(true);
    setError(null);

    try {
      let extractedContent = '';
      
      if (fileName.endsWith('.pdf')) {
        extractedContent = await processPDFFile(file);
      } else if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
        extractedContent = await processDOCXFile(file);
      }

      // 추출된 내용을 에디터에 추가
      setEditorContent(prev => prev + '\n\n' + extractedContent);
      
      // 제목이 비어있다면 파일명에서 추출
      if (!formData.topic) {
        const title = file.name.replace(/\.(pdf|docx|doc)$/i, '');
        setFormData(prev => ({ ...prev, topic: title }));
      }

    } catch (err) {
      console.error('파일 처리 실패:', err);
      setError('파일 처리 중 오류가 발생했습니다.');
    } finally {
      setIsProcessingFile(false);
    }
  };

  const processPDFFile = async (file: File): Promise<string> => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let htmlContent = '';

      // 모든 페이지에서 텍스트와 레이아웃 정보 추출
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        
        // 텍스트와 위치 정보 추출
        const textContent = await page.getTextContent();
        
        // 텍스트 아이템들을 위치별로 정렬하고 그룹화
        const textItems = textContent.items.map((item: any) => ({
          str: item.str,
          x: item.transform[4],
          y: item.transform[5],
          fontSize: item.height,
          fontName: item.fontName
        })).sort((a, b) => b.y - a.y || a.x - b.x); // Y축 내림차순, X축 오름차순

        // 텍스트를 라인별로 그룹화하여 HTML로 변환
        let currentY = -1;
        let currentLine = '';
        let fontSize = 12;
        
        for (const item of textItems) {
          if (Math.abs(item.y - currentY) > 5) { // 새로운 라인
            if (currentLine.trim()) {
              // 폰트 크기에 따라 헤딩 또는 단락으로 구분
              if (fontSize > 16) {
                htmlContent += `<h2 style="font-size: ${fontSize}px;">${currentLine.trim()}</h2>\n`;
              } else if (fontSize > 14) {
                htmlContent += `<h3 style="font-size: ${fontSize}px;">${currentLine.trim()}</h3>\n`;
              } else {
                htmlContent += `<p style="font-size: ${fontSize}px;">${currentLine.trim()}</p>\n`;
              }
            }
            currentLine = item.str;
            currentY = item.y;
            fontSize = item.fontSize;
          } else {
            currentLine += ' ' + item.str;
          }
        }
        
        // 마지막 라인 처리
        if (currentLine.trim()) {
          if (fontSize > 16) {
            htmlContent += `<h2 style="font-size: ${fontSize}px;">${currentLine.trim()}</h2>\n`;
          } else if (fontSize > 14) {
            htmlContent += `<h3 style="font-size: ${fontSize}px;">${currentLine.trim()}</h3>\n`;
          } else {
            htmlContent += `<p style="font-size: ${fontSize}px;">${currentLine.trim()}</p>\n`;
          }
        }

        // 페이지 전체를 이미지로도 추가 (레이아웃 참조용)
        try {
          const imageViewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          
          canvas.height = imageViewport.height;
          canvas.width = imageViewport.width;

          if (context) {
            await page.render({ 
              canvasContext: context, 
              viewport: imageViewport,
              canvas: canvas
            }).promise;

            const imageData = canvas.toDataURL('image/png');
            htmlContent += `<div style="margin: 20px 0; padding: 10px; background: #f9f9f9; border-left: 3px solid #ccc;">
              <small style="color: #666;">원본 레이아웃 참조 (페이지 ${pageNum}):</small><br/>
              <img src="${imageData}" alt="PDF 페이지 ${pageNum} 원본" style="max-width: 100%; height: auto; margin: 10px 0;"/>
            </div>\n`;
          }
        } catch (imgError) {
          console.warn(`페이지 ${pageNum} 이미지 추출 실패:`, imgError);
        }

        // 페이지별 구분 추가
        if (pageNum > 1) htmlContent += '<hr style="margin: 20px 0; border: 1px solid #e5e7eb;"/>\n';
      }

      return htmlContent || `PDF 파일 "${file.name}"에서 내용을 추출했지만 비어있습니다.`;
    } catch (error) {
      console.error('PDF 처리 오류:', error);
      throw new Error('PDF 파일 처리 중 오류가 발생했습니다.');
    }
  };

  const processDOCXFile = async (file: File): Promise<string> => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      
      // HTML 형태로 변환 (서식과 이미지 원본 크기 유지)
      const htmlResult = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          convertImage: mammoth.images.imgElement((image: any) => {
            return image.read("base64").then((imageBuffer: string) => {
              // mammoth에서 제공하는 모든 이미지 정보 확인
              console.log('DOCX 이미지 정보:', {
                contentType: image.contentType,
                altText: image.altText,
                filename: image.filename,
                // 사용 가능한 모든 속성 확인
                allProperties: Object.keys(image)
              });
              
              // 파일에서 추출할 때는 적절한 크기로 제한
              const result: any = {
                src: `data:${image.contentType || 'image/png'};base64,${imageBuffer}`,
                style: 'max-width: 400px; height: auto; margin: 10px 0; border-radius: 4px;'
              };
              
              // alt 텍스트가 있으면 추가
              if (image.altText) {
                result.alt = image.altText;
              }
              
              return result;
            });
          }),
          // 서식 매핑 추가
          styleMap: [
            "p[style-name='Heading 1'] => h1:fresh",
            "p[style-name='Heading 2'] => h2:fresh", 
            "p[style-name='Heading 3'] => h3:fresh",
            "b => strong",
            "i => em",
            "u => u"
          ]
        } as any
      );
      
      if (htmlResult.value.trim()) {
        return htmlResult.value;
      } else {
        // HTML이 비어있으면 텍스트만 추출
        const textResult = await mammoth.extractRawText({ arrayBuffer });
        return textResult.value || `DOCX 파일 "${file.name}"에서 내용을 추출했지만 비어있습니다.`;
      }
    } catch (error) {
      console.error('DOCX 처리 오류:', error);
      throw new Error('DOCX 파일 처리 중 오류가 발생했습니다.');
    }
  };

  const handleSaveContent = async () => {
    if (!formData.topic?.trim()) {
      setError('제목을 입력해주세요.');
      return;
    }

    if (!editorContent?.trim()) {
      setError('내용을 입력해주세요.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      // 이미지를 GCS로 업로드하고 HTML 업데이트
      console.log('Processing images in content...');
      const processedContent = await imageUploadService.processHtmlImages(
        editorContent, 
        'blog_' + formData.topic.trim().substring(0, 20).replace(/[^a-zA-Z0-9]/g, '_')
      );
      
      const manualContentData = {
        title: formData.topic.trim(),
        content: processedContent,
        category_id: formData.category_id,
        author_name: '관리자',
        author_email: 'admin@sslmo-dambi.com'
      };

      const content = await contentAPI.createManualContent(manualContentData);
      setGeneratedContent(content);
    } catch (err) {
      console.error('글 저장 실패:', err);
      setError(err instanceof Error ? err.message : '글 저장에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <WriteContainer>
      <FloatingNav>
        <NavItem>
          <a href="/blog" title="글 목록">
            📋
          </a>
        </NavItem>
        <NavItem active>
          <a href="/blog/write" title="글 작성">
            ✏️
          </a>
        </NavItem>
        <NavItem>
          <a href="/blog/manage" title="글 관리">
            ⚙️
          </a>
        </NavItem>
      </FloatingNav>
      <WriteContent>
        {!generatedContent ? (
          <WritingForm>
            <ControlSidebar>
              <SidebarContent>
                <SidebarSection>
                  <CategoryGroup>
                    <label>카테고리</label>
                    <select 
                      value={formData.category_id} 
                      onChange={(e) => setFormData(prev => ({ ...prev, category_id: parseInt(e.target.value) }))}
                    >
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </CategoryGroup>
                  
                  <TitleGroup>
                    <label>제목</label>
                    <input
                      type="text"
                      placeholder="블로그 글 제목을 입력하세요"
                      value={formData.topic}
                      onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                    />
                  </TitleGroup>
                </SidebarSection>

                <SidebarSection>
                  <FileUploadArea>
                    <label>파일에서 가져오기</label>
                    <FileUploadInput
                      type="file"
                      accept=".pdf,.docx,.doc"
                      onChange={handleFileUpload}
                      disabled={isProcessingFile}
                      id="file-upload"
                    />
                    <FileUploadLabel htmlFor="file-upload" disabled={isProcessingFile}>
                      {isProcessingFile ? (
                        <>⏳ 처리 중...</>
                      ) : (
                        <>📄 파일 업로드</>
                      )}
                    </FileUploadLabel>
                    <FileUploadHint>
                      PDF/DOCX 파일 지원
                    </FileUploadHint>
                  </FileUploadArea>
                </SidebarSection>

                {error && <ErrorMessage>{error}</ErrorMessage>}
              </SidebarContent>

              <SidebarActions>
                <button type="button" onClick={handleReset} className="reset-btn">
                  초기화
                </button>
                <button 
                  type="button" 
                  className="save-btn"
                  onClick={handleSaveContent}
                  disabled={isSaving}
                >
                  {isSaving ? '⏳ 저장 중...' : '블로그에 발행'}
                </button>
              </SidebarActions>
            </ControlSidebar>

            <EditorSection>
              <EditorWrapper>
                <CustomJoditEditor
                  content={editorContent}
                  onContentChange={setEditorContent}
                  placeholder="블로그 글 내용을 작성하거나 왼쪽에서 파일을 업로드하세요..."
                />
              </EditorWrapper>
            </EditorSection>

          </WritingForm>
        ) : (
          <PreviewSection>
            <PreviewHeader>
              <h3>✨ 저장된 블로그 글</h3>
              <PreviewActions>
                <ScoreDisplay>
                  <span className="seo-score">SEO 점수: {generatedContent.seo_score.toFixed(1)}/100</span>
                  {generatedContent.readability_score && (
                    <span className="readability-score">가독성: {generatedContent.readability_score.toFixed(1)}/100</span>
                  )}
                </ScoreDisplay>
                <ActionButtons>
                  <button onClick={handleReset} className="reset-btn">새로 작성</button>
                </ActionButtons>
              </PreviewActions>
            </PreviewHeader>

            <PreviewContent>
              <ContentMeta>
                <MetaItem>
                  <strong>카테고리:</strong> {generatedContent.category.name}
                </MetaItem>
                <MetaItem>
                  <strong>제목:</strong> {generatedContent.title}
                </MetaItem>
                {generatedContent.meta_description && (
                  <MetaItem>
                    <strong>메타 설명:</strong> {generatedContent.meta_description}
                  </MetaItem>
                )}
              </ContentMeta>

              <PreviewWrapper>
                <PreviewTitle>{generatedContent.title}</PreviewTitle>
                <ReadOnlyJoditEditor
                  content={generatedContent.content}
                  height={400}
                />
              </PreviewWrapper>
            </PreviewContent>
          </PreviewSection>
        )}
      </WriteContent>
    </WriteContainer>
  );
};

// Styled Components
const WriteContainer = styled.div`
  background: #21004D;
  position: relative;
  height: 100vh;
  overflow: hidden;
`;

const FloatingNav = styled.nav`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NavItem = styled.div<{ active?: boolean }>`
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    text-decoration: none;
    font-size: 1.2rem;
    transition: all 0.2s;
    background: ${props => props.active ? '#667eea' : 'transparent'};
    
    &:hover {
      background: ${props => props.active ? '#5a6fd8' : '#f3f4f6'};
      transform: scale(1.05);
    }
  }
`;

const WriteContent = styled.main`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  max-width: 100%;
  margin: 0;
  width: 100%;
  overflow: hidden;
`;

const WritingForm = styled.div`
  display: flex;
  height: 100%;
  background: white;
  overflow: hidden;
`;

const ControlSidebar = styled.div`
  width: 320px;
  background: #f8fafc;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 280px;
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SidebarActions = styled.div`
  padding: 1.5rem 2rem;
  background: white;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }

  button {
    width: 100%;
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 0.9rem;
  }

  .reset-btn {
    background: #f3f4f6;
    color: #374151;

    &:hover {
      background: #e5e7eb;
    }
  }

  .save-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }
  }
`;

const SidebarSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
`;

const CategoryGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #374151;
    font-size: 0.9rem;
  }

  select {
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 0.9rem;
    background: white;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: #667eea;
    }
  }
`;

const TitleGroup = styled.div`
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
    color: #374151;
    font-size: 0.9rem;
  }

  input {
    width: 100%;
    padding: 0.75rem;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 0.9rem;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: #667eea;
    }
  }
`;

const FileUploadArea = styled.div`
  label {
    display: block;
    margin-bottom: 1rem;
    font-weight: 600;
    color: #374151;
    font-size: 0.9rem;
  }
  
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  background: #fafafa;
  transition: all 0.2s;

  &:hover {
    border-color: #667eea;
    background: #f8fafc;
  }
`;

const FileUploadInput = styled.input`
  display: none;
`;

const FileUploadLabel = styled.label<{ disabled?: boolean }>`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: ${props => props.disabled ? '#f3f4f6' : '#667eea'};
  color: ${props => props.disabled ? '#9ca3af' : 'white'};
  border-radius: 8px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-weight: 600;
  transition: all 0.2s;
  font-size: 0.9rem;

  &:hover {
    background: ${props => props.disabled ? '#f3f4f6' : '#5a6fd8'};
  }
`;

const FileUploadHint = styled.p`
  margin: 0.5rem 0 0 0;
  color: #6b7280;
  font-size: 0.75rem;
`;

const EditorSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.9rem;
  border-left: 3px solid #dc2626;
`;

const PreviewSection = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
`;

const PreviewHeader = styled.div`
  padding: 2rem 3rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;

  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: #1a202c;
    font-weight: 600;
  }
`;

const PreviewActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const ScoreDisplay = styled.div`
  display: flex;
  gap: 1rem;

  span {
    background: #e0f2fe;
    color: #0369a1;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.875rem;
  }

  .seo-score {
    background: #dcfce7;
    color: #15803d;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;

  button {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
  }

  .reset-btn {
    background: #f3f4f6;
    color: #374151;

    &:hover {
      background: #e5e7eb;
    }
  }

  .save-btn {
    background: #10b981;
    color: white;

    &:hover {
      background: #059669;
    }
  }
`;

const PreviewContent = styled.div`
  padding: 3rem;
`;

const ContentMeta = styled.div`
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const MetaItem = styled.div`
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;

  &:last-child {
    margin-bottom: 0;
  }

  strong {
    color: #374151;
  }
`;

const PreviewWrapper = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
`;

const PreviewTitle = styled.h1`
  font-family: '맑은 고딕', 'Malgun Gothic', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  padding: 1rem 1rem 0.5rem;
  margin: 0;
  border-bottom: 1px solid #e5e7eb;
`;

const ArticlePreview = styled.article`
  /* 에디터와 동일한 기본 스타일 적용 */
  font-family: '맑은 고딕', 'Malgun Gothic', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  background: white;
  
  h1 {
    font-family: '맑은 고딕', 'Malgun Gothic', sans-serif;
    font-size: 2rem;
    margin: 16px 0 8px 0;
    color: #333;
    font-weight: 600;
    line-height: 1.3;
    
    &:first-child {
      margin-top: 0;
    }
  }

  .content {
    /* CSS Reset - 모든 기본 스타일 제거 */
    * {
      margin: 0 !important;
      padding: 0 !important;
      box-sizing: border-box !important;
      border: none !important;
      outline: none !important;
      background: transparent !important;
      font-family: inherit !important;
      font-size: inherit !important;
      font-weight: inherit !important;
      line-height: inherit !important;
      color: inherit !important;
      text-decoration: none !important;
      list-style: none !important;
    }

    /* 에디터와 동일한 기본 스타일만 적용 */
    font-family: '맑은 고딕', 'Malgun Gothic', sans-serif !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
    color: #333 !important;

    /* 에디터와 동일한 헤딩 스타일 */
    h1, h2, h3, h4, h5, h6 {
      font-family: '맑은 고딕', 'Malgun Gothic', sans-serif !important;
      margin: 16px 0 8px 0 !important;
      font-weight: 600 !important;
      color: #333 !important;
      background: transparent !important;
      border: none !important;
      padding: 0 !important;
    }
    
    h2 {
      font-size: 1.5rem !important;
    }
    
    h3 {
      font-size: 1.25rem !important;
    }

    /* 에디터와 동일한 문단 스타일 */
    p {
      margin: 8px 0 !important;
      color: #333 !important;
      font-size: 14px !important;
      line-height: 1.6 !important;
      background: transparent !important;
      padding: 0 !important;
    }

    /* 에디터와 동일한 목록 스타일 */
    ul, ol {
      margin: 8px 0 !important;
      padding-left: 24px !important;
      list-style: revert !important;
      background: transparent !important;
    }

    li {
      margin-bottom: 0 !important;
      color: #333 !important;
      font-size: 14px !important;
      line-height: 1.6 !important;
      background: transparent !important;
      padding: 0 !important;
    }
    
    /* 에디터와 동일한 인용문 스타일 */
    blockquote {
      border-left: 4px solid #667eea;
      margin: 16px 0;
      padding: 8px 16px;
      background: #f8fafc;
      font-style: italic;
      color: #333;
    }
    
    /* 에디터와 동일한 테이블 스타일 */
    table {
      border-collapse: collapse;
      width: 100%;
      margin: 16px 0;
      
      th, td {
        border: 1px solid #e5e7eb;
        padding: 8px 12px;
        text-align: left;
        color: #333;
      }
      
      th {
        background: #f8fafc;
        font-weight: 600;
      }
    }
    
    /* 에디터와 동일한 이미지 스타일 */
    img {
      max-width: 100%;
      height: auto;
      border-radius: 4px;
      margin: 8px 0;
      display: block;
    }
    
    /* 에디터와 동일한 링크 스타일 */
    a {
      color: #667eea;
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
    
    /* 에디터와 동일한 코드 스타일 */
    code {
      background: #f1f5f9;
      padding: 2px 4px;
      border-radius: 3px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
      color: #333;
    }
    
    pre {
      background: #f1f5f9;
      padding: 12px;
      border-radius: 6px;
      overflow-x: auto;
      margin: 16px 0;
      
      code {
        background: none;
        padding: 0;
      }
    }
  }
`;

const EditorWrapper = styled.div`
  flex: 1;
  padding: 1rem;
  
  .jodit-container {
    border: none !important;
  }
  
  .jodit-wysiwyg {
    overflow-y: auto !important;
  }
`;


export default BlogWrite; 